import { useCallback, useState, useSyncExternalStore } from "react";

import {
  ageStanding,
  parseAgeAnswer,
  readAgeAnswer,
  rememberAgeAnswer,
  subscribeAgeAnswer,
  type AgeStanding,
} from "@/engine/state/ageFloor";

/**
 * The snapshot is a string because `useSyncExternalStore` compares snapshots by
 * identity, and a parsed object would be a new one on every read. The server's
 * is empty, so a hydrating page starts where the server's did — on "unasked",
 * with the door held — and only then reads the browser.
 */
function snapshot(): string {
  const answer = readAgeAnswer();
  return answer ? JSON.stringify(answer) : "";
}

function standingOf(stored: string): AgeStanding {
  return ageStanding(
    stored ? parseAgeAnswer(JSON.parse(stored)) : null,
    Date.now(),
  );
}

/**
 * The account half, asked for rather than imported: the doors that ask the
 * question include the first screen, and the Supabase client must not ride in
 * its preloads (see `ProfileSync`).
 */
function accountSide() {
  return import("@/adapters/supabase/ageRecord").catch(() => null);
}

export interface AgeFloor {
  standing: AgeStanding;
  confirm: () => void;
  refuse: () => void;
}

/** What every door asks before it opens: the question, its answer, or a no. */
export function useAgeFloor(): AgeFloor {
  const stored = useSyncExternalStore(subscribeAgeAnswer, snapshot, () => "");
  const standing = standingOf(stored);

  const confirm = useCallback(() => {
    const at = new Date().toISOString();
    rememberAgeAnswer({ answer: "yes", at });
    void accountSide().then((record) =>
      record?.recordSignedInAgeConfirmation(at),
    );
  }, []);

  const refuse = useCallback(() => {
    rememberAgeAnswer({ answer: "no", at: new Date().toISOString() });
    void accountSide().then((record) => record?.signOutAfterRefusal());
  }, []);

  return { standing, confirm, refuse };
}

export interface AgeGatedAction {
  /** Runs the action now, or once the question is answered yes. */
  run: (action: () => void) => void;
  /** What `AgeQuestion` is drawn with. */
  question: {
    standing: AgeStanding;
    open: boolean;
    onYes: () => void;
    onNo: () => void;
  };
}

/**
 * For a door that is a press rather than a screen — signing in, creating a
 * room: the press asks first, and the action it was carries on after a yes.
 */
export function useAgeGatedAction(): AgeGatedAction {
  const age = useAgeFloor();
  const [pending, setPending] = useState<(() => void) | null>(null);
  const run = useCallback(
    (action: () => void) => {
      if (age.standing === "confirmed") action();
      else setPending(() => action);
    },
    [age.standing],
  );
  return {
    run,
    question: {
      standing: age.standing,
      open: pending !== null,
      onYes: () => {
        age.confirm();
        setPending(null);
        pending?.();
      },
      onNo: age.refuse,
    },
  };
}
